// 🔥 Firebase 기반 가격 관리 페이지 - 최고급 단순화 구현
// [배경] 레거시 탭들 제거, Firebase 실시간 관리만 제공하는 깔끔한 시스템

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
// 🔥 Firebase 기반 가격 관리 그리드 (단일 시스템)
import FirebasePriceManagementGrid from './components/priceManagement/FirebasePriceManagementGrid';

const PriceManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  // 🔍 URL 파라미터에서 브랜드와 디바이스 타입 추출
  const { brand, deviceType, isValidParams, errorMessage } = useMemo(() => {
    try {
      // URL 파라미터 읽기
      const typeParam = searchParams.get('type') || '태블릿'; // 기본값: 태블릿
      const brandParam = searchParams.get('brand') || 'Apple'; // 기본값: Apple

      console.log('🔍 URL 파라미터:', { type: typeParam, brand: brandParam });

      // 한국어 타입을 영어로 변환 (wearable 추가)
      let deviceType: 'tablet' | 'smartphone' | 'wearable';
      switch (typeParam) {
        case '태블릿':
        case 'tablet':
          deviceType = 'tablet';
          break;
        case '스마트폰':
        case 'smartphone':
          deviceType = 'smartphone';
          break;
        case '웨어러블':
        case 'wearable':
          deviceType = 'wearable';
          break;
        default:
          console.warn(`⚠️ 알 수 없는 디바이스 타입: ${typeParam}, 기본값(태블릿) 사용`);
          deviceType = 'tablet';
      }

      // 브랜드 검증 및 변환
      let brand: 'Apple' | 'Samsung' | 'LG';
      switch (brandParam.toLowerCase()) {
        case 'apple':
          brand = 'Apple';
          break;
        case 'samsung':
          brand = 'Samsung';
          break;
        case 'lg':
          brand = 'LG';
          break;
        default:
          console.warn(`⚠️ 알 수 없는 브랜드: ${brandParam}, 기본값(Apple) 사용`);
          brand = 'Apple';
      }

      return {
        brand,
        deviceType,
        isValidParams: true,
        errorMessage: null
      };
    } catch (error) {
      console.error('❌ URL 파라미터 파싱 실패:', error);
      return {
        brand: 'Apple' as const,
        deviceType: 'tablet' as const,
        isValidParams: false,
        errorMessage: 'URL 파라미터를 읽는 중 오류가 발생했습니다.'
      };
    }
  }, [searchParams]);

  // 🎯 동적 헤더 텍스트 (wearable 지원)
  const headerText = useMemo(() => {
    const deviceName = deviceType === 'tablet' ? '태블릿' :
                      deviceType === 'smartphone' ? '스마트폰' :
                      '웨어러블(AirPods)';
    const brandName = brand === 'Apple' ? 'Apple' : brand === 'Samsung' ? 'Samsung' : 'LG';
    return `${brandName} ${deviceName} 실시간 가격 관리`;
  }, [brand, deviceType]);

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* 🔥 페이지 헤더 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          실시간 가격 관리
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {headerText}
        </Typography>
      </Box>

      {/* ⚠️ 에러 표시 (URL 파라미터 문제 시) */}
      {!isValidParams && errorMessage && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>파라미터 오류:</strong> {errorMessage}
            <br />
            기본값(Apple 태블릿)으로 진행합니다.
          </Typography>
        </Alert>
      )}

      {/* 🔥 Firebase 실시간 관리 (메인 컨텐츠) */}
      <FirebasePriceManagementGrid 
            brand={brand}
            deviceType={deviceType}
      />
    </Box>
  );
};

export default PriceManagementPage;